import React, { useState, useEffect } from 'react';
import { Sparkles, X, Copy, RefreshCw, Check, ChevronDown, ChevronUp, Edit3, MessageSquare, Minimize2, Maximize2, Briefcase, SmilePlus, Shield } from 'lucide-react';
import api from '../utils/api';
import './AIDraftPanel.css';
import type {
  AIDraftPanelProps,
  Channel,
  Template,
  QuickAction,
  ConfidenceLevel,
} from '../types';


function AIDraftPanel({ thread, onClose }: AIDraftPanelProps): React.ReactElement {
  // Form state
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [templateCategory, setTemplateCategory] = useState<string>('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const channel: Channel = 'email';

  // Result state
  const [draft, setDraft] = useState<string>('');
  const [confidence, setConfidence] = useState<number>(0);
  const [reasoning, setReasoning] = useState<string>('');
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
  const [templateUsed, setTemplateUsed] = useState<string | null>(null);
  const [editedDraft, setEditedDraft] = useState<string | null>(null); // null = not edited, use getResolvedDraft()
  const [autoResolvedKeys, setAutoResolvedKeys] = useState<Set<string>>(new Set());
  const [dbResolvedKeys, setDbResolvedKeys] = useState<Set<string>>(new Set());

  // Cache state
  const [cachedDraft, setCachedDraft] = useState<any>(null);
  const [loadingCache, setLoadingCache] = useState<boolean>(true);

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [showReasoning, setShowReasoning] = useState<boolean>(false);
  const [showRefine, setShowRefine] = useState<boolean>(false);
  const [refinePrompt, setRefinePrompt] = useState<string>('');

  // Fetch templates on mount and when channel changes
  useEffect(() => {
    const fetchTemplates = async (): Promise<void> => {
      try {
        const response = await api.get(`/ai/templates?channel=${channel}`);
        if (response.data.success) {
          setTemplates(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      }
    };
    fetchTemplates();
    setTemplateCategory(''); // Reset template when channel changes
  }, [channel]);

  // Format time ago helper
  const formatTimeAgo = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.round(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return `${Math.round(diffHrs / 24)}d ago`;
  };

  // Check for cached draft on mount — if not cached, generate on the fly
  useEffect(() => {
    const loadOrGenerate = async () => {
      try {
        setLoadingCache(true);

        // Step 1: Check cache
        const cacheResponse = await api.get(`/ai/draft-cache/${thread.threadId}`);
        if (cacheResponse.data.success && cacheResponse.data.data) {
          const cached = cacheResponse.data.data;
          setCachedDraft(cached);
          setDraft(cached.draft || '');
          setEditedDraft(null);
          setConfidence(cached.confidenceScore || 0);
          setReasoning(cached.reasoning || '');
          setPlaceholders(cached.placeholders || {});
          setTemplateUsed(cached.templateUsed || null);
          setAutoResolvedKeys(new Set(Object.keys(cached.autoResolvedFields || {})));
          setDbResolvedKeys(new Set(Object.keys(cached.dbResolvedFields || {})));
          setLoadingCache(false);
          return;
        }

        // Step 2: No cache — generate on the fly
        console.log('[AIDraftPanel] No cache found, generating draft on the fly...');
        setLoading(true);
        setLoadingCache(false);

        const genResponse = await api.post('/ai/generate-draft', {
          threadId: thread.threadId,
          userPrompt: DEFAULT_PROMPT,
          templateCategory: null,
          channel
        }, { timeout: 120000 });

        if (genResponse.data.success) {
          const data = genResponse.data.data;
          setCachedDraft(data);
          setDraft(data.draft || '');
          setEditedDraft(null);
          setConfidence(data.confidenceScore || 0);
          setReasoning(data.reasoning || '');
          setPlaceholders(data.placeholders || {});
          setTemplateUsed(data.templateUsed || null);
          setAutoResolvedKeys(new Set(Object.keys(data.autoResolvedFields || {})));
          setDbResolvedKeys(new Set(Object.keys(data.dbResolvedFields || {})));
        }
      } catch (err) {
        console.error('Draft load/generate failed:', err);
      } finally {
        setLoadingCache(false);
        setLoading(false);
      }
    };

    if (thread.threadId && thread.threadId !== '__standalone__') {
      loadOrGenerate();
    } else {
      setLoadingCache(false);
    }
  }, [thread.threadId, channel]);

  // Generate draft
  const DEFAULT_PROMPT = 'Generate a professional reply addressing all topics and questions in this email thread.';

  const handleGenerate = async (prompt?: string): Promise<void> => {
    const promptText = prompt || userPrompt || DEFAULT_PROMPT;
    if (!promptText.trim()) return;

    console.log(`[handleGenerate] Prompt: "${promptText.substring(0, 80)}..." (${promptText.length} chars)`);

    setLoading(true);
    setError(null);
    setDraft('');
    setEditedDraft(null);
    setCopied(false);
    setShowRefine(false);

    try {
      const response = await api.post('/ai/generate-draft', {
        threadId: thread.threadId,
        userPrompt: promptText.trim(),
        templateCategory: templateCategory || null,
        channel
      }, {
        timeout: 60000
      });

      if (response.data.success) {
        const data = response.data.data;
        setDraft(data.draft || '');
          setEditedDraft(null);
        setConfidence(data.confidenceScore || 0);
        setReasoning(data.reasoning || '');
        setPlaceholders(data.placeholders || {});
        setTemplateUsed(data.templateUsed || null);
        setAutoResolvedKeys(new Set(Object.keys(data.autoResolvedFields || {})));
        setDbResolvedKeys(new Set(Object.keys(data.dbResolvedFields || {})));
        console.log(`[handleGenerate] Success: ${(data.draft || '').length} chars, ${Math.round(data.confidenceScore * 100)}% confidence`);
      } else {
        setError(response.data.error || 'Failed to generate draft');
        console.error(`[handleGenerate] API error: ${response.data.error}`);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const message = axiosErr.response?.data?.error || axiosErr.message || 'Failed to generate draft';
      setError(message);
      console.error(`[handleGenerate] Error: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle placeholder value change - live update draft
  const handlePlaceholderChange = (key: string, value: string): void => {
    setPlaceholders(prev => ({ ...prev, [key]: value }));
  };

  // Get the draft with placeholders replaced
  const getResolvedDraft = (): string => {
    let resolved = draft;
    Object.entries(placeholders).forEach(([key, value]) => {
      if (value && value.trim()) {
        const regex = new RegExp(`\\[${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
        resolved = resolved.replace(regex, value);
      }
    });
    return resolved;
  };

  // Get the final content to copy (edited version takes priority)
  const getFinalDraft = (): string => {
    return editedDraft !== null ? editedDraft : getResolvedDraft();
  };

  // Copy resolved draft as rich HTML to clipboard (preserves formatting in Gmail)
  const handleCopyToClipboard = async (): Promise<void> => {
    try {
      const { copyRichText } = await import('../utils/clipboardUtils');
      await copyRichText(getFinalDraft());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Regenerate (clear cache first)
  const handleRegenerate = async (): Promise<void> => {
    // Clear cache
    try {
      await api.delete(`/ai/draft-cache/${thread.threadId}`);
    } catch (e) { /* ignore */ }
    setCachedDraft(null);
    // Use current prompt or default
    handleGenerate(userPrompt || DEFAULT_PROMPT);
  };

  // Refine — save draft before clearing state
  const handleRefine = (): void => {
    if (!refinePrompt.trim()) return;
    const currentDraft = getResolvedDraft();
    const refinement = refinePrompt.trim();
    console.log(`[Refine] Draft: ${currentDraft.length} chars, Refinement: "${refinement.substring(0, 50)}"`);
    setRefinePrompt('');
    setShowRefine(false);
    // Pass compound prompt directly — don't rely on state
    const compoundPrompt = `Previous draft:\n${currentDraft}\n\nRefinement request: ${refinement}`;
    handleGenerate(compoundPrompt);
  };

  // Quick action chips
  const quickActions: QuickAction[] = [
    { id: 'rephrase', label: 'Rephrase', icon: MessageSquare, prompt: 'Rephrase the entire draft using different wording while keeping the same meaning and information. Maintain the same tone and level of detail.' },
    { id: 'shorten', label: 'Shorten', icon: Minimize2, prompt: 'Make this significantly shorter and more concise. Remove unnecessary filler words and redundant sentences. Keep only the essential information and action items.' },
    { id: 'elaborate', label: 'Elaborate', icon: Maximize2, prompt: 'Expand with more detail. Add helpful context, clearer explanations of processes, and additional relevant information that would be useful for the client.' },
    { id: 'professional', label: 'More Professional', icon: Briefcase, prompt: 'Rewrite in a more professional and formal business tone. Use polished language, proper business etiquette, and a structured format.' },
    { id: 'friendly', label: 'More Friendly', icon: SmilePlus, prompt: 'Rewrite in a warmer, friendlier tone while keeping it professional. Add a more personal touch, be more approachable, and use conversational but respectful language.' },
    { id: 'formal', label: 'Formal', icon: Shield, prompt: 'Rewrite in a strictly formal tone suitable for official or compliance-related communication. Use formal salutations, precise language, and avoid any casual expressions.' },
  ];

  const handleQuickAction = (action: QuickAction): void => {
    const resolvedDraft = getResolvedDraft();
    const compoundPrompt = `Previous draft:\n${resolvedDraft}\n\nInstruction: ${action.prompt}`;
    handleGenerate(compoundPrompt);
  };

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Confidence level
  const getConfidenceLevel = (score: number): ConfidenceLevel => {
    if (score >= 0.7) return 'high';
    if (score >= 0.5) return 'medium';
    return 'low';
  };

  const confidenceLevel = getConfidenceLevel(confidence);
  const placeholderEntries = Object.entries(placeholders);
  const unfilledCount = placeholderEntries.filter(([, v]) => !v || !v.trim()).length;

  return (
    <div className="ai-draft-overlay" onClick={onClose}>
      <div className="ai-draft-panel" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-draft-header">
          <div className="ai-draft-header-left">
            <Sparkles size={20} className="sparkle-icon" />
            <div className="ai-draft-header-info">
              <h3>AI Draft Generator</h3>
              <div className="thread-subject">
                {thread.latestEmail?.subject || 'No Subject'}
              </div>
            </div>
          </div>
          <button className="ai-draft-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="ai-draft-body">
          {/* Loading Cache Check */}
          {loadingCache && thread.threadId !== '__standalone__' && (
            <div className="ai-loading" style={{ padding: '16px' }}>
              <div className="ai-loading-dots">
                <div className="ai-loading-dot"></div>
                <div className="ai-loading-dot"></div>
                <div className="ai-loading-dot"></div>
              </div>
              <div className="ai-loading-text">Loading draft...</div>
            </div>
          )}

{/* Template Selector */}
          <div className="ai-template-selector">
            <label>Template (optional)</label>
            <select
              className="ai-template-select"
              value={templateCategory}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTemplateCategory(e.target.value)}
            >
              <option value="">Auto-detect from thread</option>
              {templates.map(t => (
                <option key={t.category} value={t.category}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Prompt Input */}
          <div className="ai-prompt-section">
            <label>What would you like to draft?</label>
            <textarea
              className="ai-prompt-textarea"
              placeholder="e.g., Draft a follow-up about MISA license renewal, inform the client about CR renewal process..."
              value={userPrompt}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setUserPrompt(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleGenerate();
                }
              }}
            />
          </div>

          {/* Cached Draft Banner */}
          {cachedDraft && draft && !loading && (
            <div className="ai-cached-banner">
              <Sparkles size={14} />
              <span>
                Draft auto-generated {cachedDraft.generatedAt ? formatTimeAgo(cachedDraft.generatedAt) : 'recently'}
                {cachedDraft.confidenceScore ? ` — ${Math.round(cachedDraft.confidenceScore * 100)}% confidence` : ''}
              </span>
            </div>
          )}

          {/* Generate Button */}
          <button
            className="ai-generate-btn"
            onClick={() => handleGenerate()}
            disabled={loading || !userPrompt.trim()}
          >
            <Sparkles size={16} />
            {loading ? 'Generating...' : 'Generate Draft'}
          </button>

          {/* Loading Animation */}
          {loading && (
            <div className="ai-loading">
              <div className="ai-loading-dots">
                <div className="ai-loading-dot"></div>
                <div className="ai-loading-dot"></div>
                <div className="ai-loading-dot"></div>
              </div>
              <div className="ai-loading-text">Analyzing thread and generating draft...</div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="ai-error">{error}</div>
          )}

          {/* Draft Result */}
          {draft && !loading && (
            <div className="ai-draft-result">
              {/* Confidence Meter */}
              <div className="ai-confidence">
                <div className="ai-confidence-header">
                  <span className="ai-confidence-label">Confidence</span>
                  <span className={`ai-confidence-value ${confidenceLevel}`}>
                    {Math.round(confidence * 100)}%
                  </span>
                </div>
                <div className="ai-confidence-bar">
                  <div
                    className={`ai-confidence-fill ${confidenceLevel}`}
                    style={{ width: `${confidence * 100}%` }}
                  />
                </div>
              </div>

              {/* Template Used Badge */}
              {templateUsed && (
                <div className="ai-template-badge">
                  <Sparkles size={12} />
                  Template: {templateUsed}
                </div>
              )}

              {/* Reasoning (collapsible) */}
              {reasoning && (
                <div
                  className="ai-reasoning"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowReasoning(!showReasoning)}
                >
                  {showReasoning ? (
                    <>
                      <ChevronUp size={12} style={{ display: 'inline', marginRight: 4 }} />
                      {reasoning}
                    </>
                  ) : (
                    <>
                      <ChevronDown size={12} style={{ display: 'inline', marginRight: 4 }} />
                      {reasoning.substring(0, 80)}...
                    </>
                  )}
                </div>
              )}

              {/* Draft Text — Editable */}
              <textarea
                className="ai-draft-text ai-draft-editable"
                value={editedDraft !== null ? editedDraft : getResolvedDraft()}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedDraft(e.target.value)}
                rows={12}
              />

              {/* Quick Action Chips */}
              <div className="ai-quick-actions">
                <div className="ai-quick-actions-label">Adjust tone:</div>
                <div className="ai-quick-actions-chips">
                  {quickActions.map((action) => {
                    const IconComponent = action.icon;
                    return (
                      <button
                        key={action.id}
                        className="ai-quick-chip"
                        onClick={() => handleQuickAction(action)}
                        disabled={loading}
                        title={action.prompt}
                      >
                        <IconComponent size={13} />
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Placeholder Editor */}
              {placeholderEntries.length > 0 && (
                <div className="ai-placeholders">
                  <div className="ai-placeholders-title">
                    <Edit3 size={14} />
                    {unfilledCount > 0
                      ? `Fill in placeholders (${unfilledCount} remaining)`
                      : 'All placeholders filled'}
                  </div>
                  {placeholderEntries.map(([key, value]) => {
                    const isFromThread = autoResolvedKeys.has(key);
                    const isFromDb = dbResolvedKeys.has(key);
                    const isFilled = value && value.trim();
                    return (
                      <div key={key} className="ai-placeholder-field">
                        <span className="ai-placeholder-label">[{key}]</span>
                        <input
                          type="text"
                          className={`ai-placeholder-input ${isFilled ? (isFromDb ? 'filled-db' : isFromThread ? 'filled-thread' : 'filled') : ''}`}
                          placeholder={`Enter ${key}...`}
                          value={value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePlaceholderChange(key, e.target.value)}
                        />
                        {isFilled && isFromDb && <span className="ai-placeholder-source db">DB</span>}
                        {isFilled && isFromThread && !isFromDb && <span className="ai-placeholder-source thread">Thread</span>}
                        {isFilled && !isFromDb && !isFromThread && <Check size={14} className="ai-placeholder-check" />}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Single Copy Button + Regenerate + Refine */}
              <div className="ai-actions">
                <button
                  className={`ai-btn ${copied ? 'ai-btn-success' : 'ai-btn-primary'}`}
                  onClick={handleCopyToClipboard}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </button>
                <button
                  className="ai-btn ai-btn-secondary"
                  onClick={handleRegenerate}
                >
                  <RefreshCw size={14} />
                  Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIDraftPanel;
